import { addNotification } from "portal/state/notifications";
import {
  AdminCarbonCrop,
  carbonCropsEndpoints,
} from "portal/state/portal/carbonCrops";
import {
  adminCarbonCropsReadStatement,
  useCanUpdateAdminCarbonCrops,
} from "portal/views/admin/permissions";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import {
  CARBON_CROP_TRANSLATION_LANGUAGES,
  CarbonCropTranslationLanguage,
  isCarbonCropTranslationLanguage,
} from "./languages";
import { DateTime } from "luxon";
import { Field, Form, Formik } from "formik";
import { getAdminCarbonCropPath } from "portal/utils/routing";
import { isEqual } from "portal/utils/equality";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Loading } from "portal/components/Loading";
import { object, string } from "yup";
import { skipToken } from "@reduxjs/toolkit/query";
import { TextField } from "formik-mui";
import { toDisplayName } from "common/utils/i18n";
import { useDispatch } from "react-redux";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { VeselkaCropAssociationsField } from "./VeselkaCropAssociationsField";
import { withAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { withErrorBoundary } from "portal/components/ErrorBoundary";
import ArchiveIcon from "@mui/icons-material/ArchiveOutlined";
import CarbonCropIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import ParentIcon from "@mui/icons-material/ArrowBackOutlined";
import React, { FC, useCallback, useMemo, useState } from "react";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import UnarchiveIcon from "@mui/icons-material/UnarchiveOutlined";

type TranslationDraft = Record<CarbonCropTranslationLanguage, string>;

type EditorDraft = {
  name: string;
  translations: TranslationDraft;
  veselkaCropIds: string[];
};

const emptyTranslations = (): TranslationDraft => {
  const translations: Partial<TranslationDraft> = {};
  for (const language of CARBON_CROP_TRANSLATION_LANGUAGES) {
    translations[language] = "";
  }
  // Every CarbonCropTranslationLanguage key is set above.
  return translations as TranslationDraft;
};

const draftFromCrop = (crop: AdminCarbonCrop): EditorDraft => {
  const translations = emptyTranslations();
  for (const entry of crop.translations) {
    if (isCarbonCropTranslationLanguage(entry.language)) {
      translations[entry.language] = entry.translation;
    }
  }
  return {
    name: crop.name,
    translations,
    veselkaCropIds: [...crop.veselkaCropIds],
  };
};

const normalizedDraft = (draft: EditorDraft): EditorDraft => ({
  name: draft.name.trim(),
  translations: Object.fromEntries(
    CARBON_CROP_TRANSLATION_LANGUAGES.map((language) => [
      language,
      draft.translations[language].trim(),
    ])
  ) as TranslationDraft,
  veselkaCropIds: draft.veselkaCropIds,
});

const hasSaveableChanges = (
  values: EditorDraft,
  initial: EditorDraft
): boolean =>
  !isEqual(normalizedDraft(values), normalizedDraft(initial), {
    orderedArrays: false,
  });

const _AdminCarbonCropPage: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const canUpdate = useCanUpdateAdminCarbonCrops();

  const onBack = useCallback(() => {
    navigate(getAdminCarbonCropPath());
  }, [navigate]);

  const {
    data: crop,
    isError,
    error,
    isFetching,
  } = useQueryPopups(
    carbonCropsEndpoints.getCarbonCrop.useQuery(id ?? skipToken)
  );
  const {
    data: unassociatedVeselkaCrops,
    isFetching: loadingVeselkaCrops,
    isError: loadingVeselkaCropsError,
  } = useQueryPopups(
    carbonCropsEndpoints.listUnassociatedVeselkaCrops.useQuery(
      id ? { includeAssociatedWithCarbonCropId: id } : skipToken
    )
  );
  const [updateCarbonCrop] = useMutationPopups(
    carbonCropsEndpoints.updateCarbonCrop.useMutation()
  );
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const initialValues = useMemo(
    () =>
      crop
        ? draftFromCrop(crop)
        : {
            name: "",
            translations: emptyTranslations(),
            veselkaCropIds: [],
          },
    [crop]
  );

  const veselkaCropOptions = unassociatedVeselkaCrops?.crops ?? [];

  const formatTimestamp = (value: string | undefined): string => {
    if (!value) {
      return "";
    }
    return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT, {
      locale: i18n.language,
    });
  };

  if (!id) {
    return <Navigate to={getAdminCarbonCropPath()} replace />;
  }

  if (!crop) {
    return <Loading failed={isError} error={error} loading={isFetching} />;
  }

  const isArchived = Boolean(crop.archivedAt);

  const setArchived = async (archive: boolean): Promise<boolean> => {
    if (!canUpdate) {
      return false;
    }
    setIsArchiving(true);
    try {
      const { error: updateError } = await updateCarbonCrop({
        id: crop.uuid,
        name: crop.name,
        translations: [],
        veselkaCropIds: crop.veselkaCropIds,
        archive,
      });
      if (updateError) {
        return false;
      }
      dispatch(
        addNotification({
          message: archive
            ? t("components.carbonCrop.CarbonCropEditor.archivedSuccess")
            : t("components.carbonCrop.CarbonCropEditor.unarchivedSuccess"),
          variant: "success",
        })
      );
      return true;
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Formik<EditorDraft>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={object({
          name: string().trim().required(),
        })}
        onSubmit={async (values) => {
          if (!canUpdate || !hasSaveableChanges(values, initialValues)) {
            return;
          }
          const toBeSaved = normalizedDraft(values);
          const previous = normalizedDraft(initialValues);
          const translations = CARBON_CROP_TRANSLATION_LANGUAGES.flatMap(
            (language) => {
              const translation = toBeSaved.translations[language];
              const prior = previous.translations[language];
              if (translation === prior) {
                return [];
              }
              return [{ language, translation }];
            }
          );
          const { error: updateError } = await updateCarbonCrop({
            id: crop.uuid,
            name: toBeSaved.name,
            translations,
            veselkaCropIds: toBeSaved.veselkaCropIds,
          });
          if (updateError) {
            return;
          }
          dispatch(
            addNotification({
              message: t("utils.actions.savedLong", {
                subject: t("models.carbonCrops.carbonCrop_title_one"),
              }),
              variant: "success",
            })
          );
        }}
      >
        {({ submitForm, isSubmitting, values, setFieldValue }) => (
          <Form className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <IconButton
                  component={Link}
                  to={getAdminCarbonCropPath()}
                  aria-label={t("utils.actions.backShort")}
                  onClick={onBack}
                >
                  <ParentIcon />
                </IconButton>
                <CarbonCropIcon className="shrink-0" color="inherit" />
                <Typography variant="h4" className="truncate">
                  {crop.name}
                </Typography>
                {isArchived && (
                  <Chip
                    className="shrink-0"
                    size="small"
                    label={t("models.carbonCrops.fields.archived")}
                  />
                )}
              </div>
              <div className="flex justify-end gap-2 shrink-0">
                <Button
                  variant="text"
                  color="inherit"
                  disabled={isSubmitting || isArchiving}
                  onClick={onBack}
                >
                  {t("utils.actions.cancel")}
                </Button>
                {canUpdate && isArchived && (
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={isSubmitting || isArchiving}
                    loading={isArchiving}
                    onClick={async () => {
                      await setArchived(false);
                    }}
                    startIcon={<UnarchiveIcon />}
                  >
                    {t("utils.actions.unarchive")}
                  </Button>
                )}
                {canUpdate && !isArchived && (
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={isSubmitting || isArchiving}
                    onClick={() => setArchiveConfirmOpen(true)}
                    startIcon={<ArchiveIcon />}
                  >
                    {t("utils.actions.archive")}
                  </Button>
                )}
                {canUpdate && (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={
                      !hasSaveableChanges(values, initialValues) ||
                      !values.name.trim() ||
                      isArchiving
                    }
                    loading={isSubmitting}
                    onClick={submitForm}
                    startIcon={<SaveIcon />}
                  >
                    {t("utils.actions.save")}
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              <Paper className="flex flex-col gap-4 p-4">
                <Field
                  component={TextField}
                  name="name"
                  label={t("models.carbonCrops.fields.name")}
                  disabled={!canUpdate || isSubmitting || isArchiving}
                />

                <VeselkaCropAssociationsField
                  value={values.veselkaCropIds}
                  onChange={(veselkaCropIds) =>
                    setFieldValue("veselkaCropIds", veselkaCropIds)
                  }
                  options={veselkaCropOptions}
                  loading={loadingVeselkaCrops}
                  loadingError={loadingVeselkaCropsError}
                  disabled={!canUpdate || isSubmitting || isArchiving}
                />

                <div className="flex flex-col gap-1">
                  {crop.createdAt && (
                    <Typography variant="body2" color="textSecondary">
                      {t("components.carbonCrop.CarbonCropEditor.createdAt", {
                        timestamp: formatTimestamp(crop.createdAt),
                      })}
                    </Typography>
                  )}
                  {crop.updatedAt && (
                    <Typography variant="body2" color="textSecondary">
                      {t("components.carbonCrop.CarbonCropEditor.updatedAt", {
                        timestamp: formatTimestamp(crop.updatedAt),
                      })}
                    </Typography>
                  )}
                  {crop.archivedAt && (
                    <Typography variant="body2" color="textSecondary">
                      {t("components.carbonCrop.CarbonCropEditor.archivedAt", {
                        timestamp: formatTimestamp(crop.archivedAt),
                      })}
                    </Typography>
                  )}
                </div>
              </Paper>

              <Paper className="flex flex-col gap-4 p-4">
                <Typography variant="subtitle2">
                  {t("models.carbonCrops.fields.translations")}
                </Typography>

                <div className="flex flex-col gap-3">
                  {CARBON_CROP_TRANSLATION_LANGUAGES.map((language) => (
                    <Field
                      key={language}
                      component={TextField}
                      name={`translations.${language}`}
                      label={toDisplayName(language)}
                      disabled={!canUpdate || isSubmitting || isArchiving}
                    />
                  ))}
                </div>
              </Paper>
            </div>
          </Form>
        )}
      </Formik>
      <Dialog
        open={archiveConfirmOpen}
        onClose={() => {
          if (isArchiving) {
            return;
          }
          setArchiveConfirmOpen(false);
        }}
      >
        <DialogTitle>{t("utils.actions.archive")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("components.carbonCrop.CarbonCropEditor.archiveConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            color="inherit"
            disabled={isArchiving}
            onClick={() => setArchiveConfirmOpen(false)}
          >
            {t("utils.actions.cancel")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={isArchiving}
            loading={isArchiving}
            onClick={async () => {
              if (await setArchived(true)) {
                setArchiveConfirmOpen(false);
              }
            }}
            startIcon={<ArchiveIcon />}
          >
            {t("utils.actions.archive")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export const AdminCarbonCropPage = withAuthorizationRequired(
  adminCarbonCropsReadStatement,
  withErrorBoundary(
    { i18nKey: "components.carbonCrop.CarbonCropEditor.errors.load" },
    _AdminCarbonCropPage
  )
);
